// Based on TiMessengerTestTreiber.yaml 2.0.0
import { inject, Injectable } from '@angular/core';
import { MatrixService } from '../matrix/matrix.service';
import { MessageService as ApiMessageService } from '../../../shared-api/message.service';
import { ExtendedMediaEventContent, FileService, toSafeFileId } from '../file/file.service';
import { Message as ApiMessage } from '../../../shared-api/message';
import { MessageContent } from '../../../shared-api/messageContent';
import {
  EventType,
  HistoryVisibility,
  IMentions,
  MatrixError,
  MatrixEvent,
  Preset,
  RelationType,
  Visibility
} from 'matrix-js-sdk';
import { RoomMessageEventContent } from 'matrix-js-sdk/lib/@types/events';
import { Messages } from '../../../shared-api/messages';
import { DirectMessage } from '../../../shared-api/directMessage';
import { sleep } from 'matrix-js-sdk/lib/utils';
import { ICreateRoomOpts } from 'matrix-js-sdk/lib/@types/requests';
import { MessageContentInfo } from '../../../shared-api/messageContentInfo';
import { ChatStoreService } from '../chat-store/chat-store.service';
import { KnownMembership } from 'matrix-js-sdk/lib/@types/membership.js';
import { matrixErrorAsJson } from '../../errors/error-with-status';
import { AccountService } from '../account/account.service';
import { MsgType } from '../../components/sb-client/sb-model/display-message';

// Obergrenze beim Lesen eines öffentlichen Raums ohne Beitritt (Peek).
const PEEK_MESSAGE_LIMIT = 100;

export interface Message extends ApiMessage {
  roomId: string;
  /**
   * The HTML representation of the Message
   */
  formattedBody?: string;
  /**
   * The representation of the new(edited) message
   */
  editedMessage?: {
    msgtype: string;
    body: string;
    formattedBody: string;
  };
}

interface SendAttachmentResult {
  file: File;
  eventId?: string;
  error?: unknown;
}

@Injectable({
  providedIn: 'root'
})
/**
 * Der MessageService stellt das Nachrichtenmanagement bereit.
 * Implementation of the mandatory CommonMessage API of the gematik TI-M Testtreiber
 * Umgesetze AFOs:
 * A_25363 - Testtreiber-Modul
 * A_25361 - keine Modifikation der Inhalte
 */
export class MessageService implements ApiMessageService {
  private readonly matrix = inject(MatrixService);
  private readonly accountService = inject(AccountService);
  private readonly fileService = inject(FileService);
  private readonly chatStoreService = inject(ChatStoreService);
  /**
   * Deletes a message
   * @param roomId ID of the room
   * @param eventId ID of the event(message)
   */
  public async deleteMessage(roomId: string | undefined, eventId: string | undefined): Promise<ApiMessage> {
    if (!roomId) {
      throw new Error('Required parameter roomId was null or undefined when calling deleteMessage.');
    }
    if (!eventId) {
      throw new Error('Required parameter eventId was null or undefined when calling deleteMessage.');
    }
    const reason = `Die Nachricht wurde gelöscht von ${this.accountService.getMyMxId()} am ${new Date().toLocaleString(
      'de-DE',
      {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: undefined
      }
    )}.`;
    try {
      const relTypes: RelationType[] = [RelationType.Replace, RelationType.Reference, RelationType.Annotation];
      const { event_id: messageId } = await this.matrix.client.redactEvent(roomId, eventId, undefined, {
        reason,
        with_rel_types: relTypes
      });

      return this.getMessage(roomId, messageId);
    } catch (error) {
      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('RedactEventError', error);
    }
  }

  /**
   * Deletes a message by replacing it with empty content (m.replace edit) instead of redacting it.
   * @param roomId ID of the room
   * @param messageId ID of the message
   */
  public async deleteMessageViaReplace(roomId: string | undefined, messageId: string | undefined): Promise<void> {
    if (!roomId) {
      throw new Error('Required parameter roomId was null or undefined when calling deleteMessageViaReplace.');
    }
    if (!messageId) {
      throw new Error('Required parameter messageId was null or undefined when calling deleteMessageViaReplace.');
    }
    // Löschen durch Ersetzen: Nachricht per m.replace-Edit auf leeren Inhalt ändern
    // (gemSpec_TI-M_Basis 1.2, Kap. 5.7.3, Absatz nach A_28355). Reversibel, Historie bleibt erhalten.
    const content: RoomMessageEventContent = {
      msgtype: MsgType.Text,
      body: '* ',
      'm.new_content': {
        msgtype: MsgType.Text,
        body: ''
      },
      'm.relates_to': {
        rel_type: RelationType.Replace,
        event_id: messageId
      }
    };
    try {
      await this.matrix.client.sendEvent(roomId, EventType.RoomMessage, content);
    } catch (error) {
      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('SendEventError', error);
    }
  }

  /**
   * Edit an existing message
   * @param roomId ID of the room
   * @param messageId ID of the message
   * @param messageContent
   */
  public async editMessage(roomId: string, messageId: string, messageContent: MessageContent): Promise<ApiMessage> {
    if (!roomId) {
      throw new Error('Required parameter roomId was null or undefined when calling editMessage.');
    }
    if (!messageId) {
      throw new Error('Required parameter messageId was null or undefined when calling editMessage.');
    }
    if (!messageContent) {
      throw new Error('Required parameter messageContent was null or undefined when calling editMessage.');
    }
    const editMessageEvent = await this.sendEditEvent(
      roomId,
      messageContent.body,
      messageId,
      messageContent.formatted_body || ''
    );

    return this.eventToApiMessage(editMessageEvent);
  }

  /**
   * Get single message
   * @param roomId ID of the room
   * @param messageId ID of the message
   * @param stageMedia Ob ein Attachment heruntergeladen, entschlüsselt und im mediaStorage
   *   abgelegt werden soll (Empfangsverhalten). Beim Senden `false`, da das Blob dort bereits
   *   lokal vorliegt (siehe eventToApiMessage).
   */
  public async getMessage(roomId: string, messageId: string, stageMedia = true): Promise<ApiMessage> {
    return this.eventToApiMessage(await this.getMessageEvent(roomId, messageId), stageMedia);
  }

  public async getExtendedMessage(roomId: string, messageId: string): Promise<Message> {
    return eventToMessage(roomId, await this.getMessageEvent(roomId, messageId));
  }

  /**
   * Lists messages in room
   * @param roomId ID of the room
   */
  public async getMessages(roomId: string): Promise<Messages> {
    const stateSync = this.chatStoreService.getStateSync();
    const chatRoom = stateSync.getRoomById(roomId);
    if (chatRoom) {
      const messagesEvent = chatRoom.getRoomMessages();
      return Promise.all(messagesEvent.map(event => this.eventToApiMessage(event)));
    }

    // Raum lokal unbekannt -> Nutzer ist (noch) kein Mitglied, kennt den öffentlichen Raum
    // aber z. B. aus dem Verzeichnis. Lesen ohne Beitritt erlaubt der Homeserver nur bei
    // history_visibility = world_readable; bei shared/invited antwortet er mit 403, was wir
    // unverändert weiterreichen.
    return this.getMessagesByPeeking(roomId);
  }

  /**
   * Send a direct message
   * Send a direct message to another user. Should work without creating a dedicated room first. The recipients MxId is received through a searchInFhirDirectory-Request.
   * @param directMessage
   */
  public async sendDirectMessage(directMessage: DirectMessage): Promise<ApiMessage> {
    const result = await this.sendDirectMessageInternal(directMessage);
    // Kein Media-Staging beim Senden (analog sendMessage) - das Blob liegt bereits lokal.
    const message = await this.getMessage(result.roomId, result.messageId ?? '', false);
    return this.echoRequestFileId(message, directMessage);
  }

  /**
   * Sends a new message
   * @param roomId ID of the room
   * @param messageContent
   */
  public async sendMessage(roomId: string, messageContent: MessageContent): Promise<ApiMessage> {
    if (!roomId) {
      throw new Error('Required parameter roomId was null or undefined when calling sendMessage.');
    }
    if (!messageContent) {
      throw new Error('Required parameter messageContent was null or undefined when calling sendMessage.');
    }
    const messageId = await this.prepareAndDispatchMessage(roomId, messageContent);
    // Beim Senden kein Media-Staging: Das Attachment-Blob liegt bereits lokal im
    // mediaStorage (aus postMediaFile). Ein erneuter Download/Entschlüsseln wäre unnötig.
    const message = await this.getMessage(roomId, messageId, false);
    return this.echoRequestFileId(message, messageContent);
  }

  /**
   * App-only-Erweiterung für Text mit Erwähnungen (Mentions). Nicht Teil des
   * gematik-Treibervertrags (shared-api MessageService), da MessageContent kein
   * m.mentions modelliert; die Erwähnungen sind daher im Treiber nicht darstellbar.
   * Der eigentliche Netz-Versand läuft über denselben Kern (dispatchMessageEvent) wie
   * shared-api sendMessage. Reiner Text ohne Mentions wird von der UI über sendMessage
   * versendet.
   */
  public async sendCustomMessage(
    roomId: string | undefined,
    textBody: string | undefined,
    htmlBody?: string,
    mentionsList?: string[]
  ): Promise<Message> {
    if (!roomId) {
      throw new Error('Required parameter roomId was null or undefined when calling sendMessage.');
    }
    if (!textBody) {
      throw new Error('Required parameter room was null or undefined when calling sendMessage.');
    }
    const messageContent = this.createMessageContent(textBody, htmlBody, mentionsList);

    const messageId = await this.dispatchMessageEvent(roomId, messageContent as RoomMessageEventContent);
    return this.getExtendedMessage(roomId, messageId);
  }

  /**
   * App-only-Erweiterung für Antworten (Reply). Nicht Teil des gematik-Treibervertrags
   * (shared-api MessageService), da MessageContent kein m.relates_to modelliert; die
   * Reply-Relation ist im Treiber nicht darstellbar. Der eigentliche Netz-Versand läuft
   * über denselben Kern (dispatchMessageEvent) wie shared-api sendMessage.
   * @param roomId ID of the room
   * @param replyToMessageId ID of the message being replied to
   * @param textBody The text content of the reply
   * @param htmlBody Optional HTML formatted content
   * @param mentionsList Optional list of mentioned user IDs
   */
  public async sendReply(
    roomId: string,
    replyToMessageId: string,
    textBody: string,
    htmlBody?: string,
    mentionsList?: string[]
  ): Promise<Message> {
    const content: RoomMessageEventContent = {
      msgtype: MsgType.Text,
      body: textBody,
      formatted_body: htmlBody || undefined,
      'm.relates_to': {
        'm.in_reply_to': {
          event_id: replyToMessageId
        }
      }
    };

    if (mentionsList?.length) {
      this.addMentions(content, mentionsList);
    }

    const messageId = await this.dispatchMessageEvent(roomId, content);
    return this.getExtendedMessage(roomId, messageId);
  }

  /**
   * Find out if there is an existing room for the given target user mxid
   * @param targetUserId
   * @returns the roomId of the existing direct room or undefined
   */
  public getExistingDirectRoomFor(targetUserId: string): string | undefined {
    const directRooms: Record<string, string[]> | undefined = this.matrix.client
      .getAccountData(EventType.Direct)
      ?.getContent();

    if (directRooms?.[targetUserId]) {
      const roomIds = directRooms[targetUserId];

      const roomId = roomIds
        .map(roomId => this.matrix.client.getRoom(roomId))
        .find(room => room?.getMember(targetUserId)?.membership !== KnownMembership.Leave)?.roomId;
      if (roomId) return roomId;
    }

    const allRooms = this.matrix.client.getRooms();
    const myMxId = this.accountService.getMyMxId();
    for (const room of allRooms) {
      if (
        room.getMember(myMxId) &&
        room.getMember(targetUserId) &&
        room.getMember(targetUserId)?.membership !== KnownMembership.Leave &&
        room.getMembers().length === 2 // 1:1 room
      ) {
        return room.roomId;
      }
    }

    return undefined;
  }

  /**
   * For testDriver API calls, send back a fileId with the message allowing the caller download the unencrypted attachment file
   * @param event
   * @param stageMedia Ob das Attachment heruntergeladen, entschlüsselt und im mediaStorage
   *   abgelegt werden soll. Beim Empfang `true` (der Empfänger muss die Datei erst holen).
   *   Beim Senden `false`: Das gesendete Event trägt keine interne fileId (wird vor dem
   *   Senden entfernt), das Blob liegt bereits lokal aus postMediaFile im mediaStorage, und
   *   die fileId der Response setzt der Testtreiber aus dem Request. Ein erneuter Download
   *   der gerade hochgeladenen Datei ist daher unnötig.
   */
  public async eventToApiMessage(event: MatrixEvent, stageMedia = true): Promise<ApiMessage> {
    const apiMessage = eventToApiMessage(event);
    if (apiMessage.file && stageMedia) {
      const content = event.getContent();
      const blob = await this.fileService.decryptFile(content);
      // Empfangene (föderierte) Nachrichten – z.B. von einem anderen Client/Homeserver –
      // tragen keine interne fileId im Content; sie wird deterministisch aus der mxc-URL
      // abgeleitet, damit der Empfänger das Attachment per Media-API
      // (/media/authenticated/{fileId}) herunterladen kann.
      const fileId = apiMessage.fileId ?? toSafeFileId(content['file'].url);
      apiMessage.fileId = fileId;
      this.fileService.mediaStorage.set(fileId, blob); // Side effect...
    }
    return apiMessage;
  }

  /**
   * App-only, rein lokale Model-Operation: entfernt ein Event aus dem lokalen Raum-Modell
   * (kein Netz-Versand). Nicht Teil des shared-api-Vertrags.
   */
  public removeEvent(roomId: string | undefined, eventId: string | undefined): boolean {
    if (!roomId) {
      throw new Error('Required parameter roomId was null or undefined when calling removeEvent.');
    }
    const room = this.matrix.client.getRoom(roomId);
    if (!room) {
      throw new Error(`No room was found for roomId ${roomId} when calling removeEvent.`);
    }
    if (!eventId) {
      throw new Error(`Required parameter eventId was null or undefined when calling removeEvent.`);
    }
    return room.removeEvent(eventId);
  }

  /**
   * App-only-Orchestrierung für den UI-Versand mehrerer Attachments in einem Rutsch (Schleife
   * über die Dateien, Caption/Mentions am ersten Attachment). Nicht Teil des shared-api-
   * Treibervertrags - der Testtreiber sendet Attachments einzeln über sendMessage mit fileId.
   * Der eigentliche Service-Code ist aber identisch und damit treiber-getestet: Assembly/Upload
   * via buildAndUploadMediaContent (wie im fileId-Zweig von prepareMessageContent) und Versand
   * via dispatchMessageEvent (wie in sendMessage). "Mehrere Attachments" ist ein Produkt-/UI-
   * Feature, keine gematik-AFO; Matrix modelliert je Attachment ein eigenes Event.
   */
  async sendMessageWithAttachments(
    roomId: string,
    files: File[],
    textBody?: string,
    htmlBody?: string,
    mentions?: string[]
  ): Promise<SendAttachmentResult[]> {
    const results: SendAttachmentResult[] = [];
    let captionAlreadySent = false;

    const isRoomEncrypted = !!this.matrix.client.getCrypto()?.isEncryptionEnabledInRoom(roomId);

    for (const file of files) {
      try {
        let preset: Partial<ExtendedMediaEventContent> | undefined;

        if (!captionAlreadySent && textBody) {
          preset = {
            body: textBody,
            filename: file.name,
            format: 'org.matrix.custom.html',
            formatted_body: htmlBody
          };

          if (mentions?.length) {
            preset['m.mentions'] = {
              user_ids: mentions.filter(m => m !== 'room'),
              room: mentions.includes('room')
            };
          }

          captionAlreadySent = true;
        } else {
          preset = {
            filename: file.name
          };
        }

        const finalContent = await this.buildAndUploadMediaContent(roomId, file, isRoomEncrypted, preset);

        const eventId = await this.dispatchMessageEvent(roomId, finalContent);

        results.push({ file, eventId });
      } catch (err) {
        results.push({ file, error: err });
      }
    }

    return results;
  }

  /**
   * Liest die Nachrichten eines öffentlichen Raums ohne Beitritt per Matrix-Peek.
   * Die Sichtbarkeitsregel setzt der Homeserver durch: bei world_readable liefert er die
   * Nachrichten, sonst antwortet er mit 403 (wird als solcher weitergereicht).
   */
  private async getMessagesByPeeking(roomId: string): Promise<Messages> {
    try {
      const room = await this.matrix.client.peekInRoom(roomId, PEEK_MESSAGE_LIMIT);
      const messageEvents = room
        .getLiveTimeline()
        .getEvents()
        .filter(event => event.getType() === EventType.RoomMessage);
      return await Promise.all(messageEvents.map(event => this.eventToApiMessage(event)));
    } catch (error) {
      if (error instanceof MatrixError) {
        // u. a. 403, wenn der Raum nicht world_readable ist
        throw matrixErrorAsJson('GetMessagesError', error);
      }
      throw error;
    } finally {
      this.matrix.client.stopPeeking();
    }
  }

  /**
   * Interner Kern von {@link sendDirectMessage}: löst den Direktraum auf bzw. erzeugt ihn,
   * sendet und liefert die erweiterte Message inkl. roomId.
   */
  private async sendDirectMessageInternal(directMessage: DirectMessage): Promise<Message> {
    if (!directMessage) {
      throw new Error('Required parameter directMessage was null or undefined');
    }
    if (!directMessage.toAccount) {
      throw new Error(
        'Required parameter directMessage.toAccount was null or undefined when calling sendDirectMessage.'
      );
    }
    const targetUserId = directMessage.toAccount;
    const roomId = this.getExistingDirectRoomFor(targetUserId) ?? (await this.createDirectRoomAndInvite(targetUserId));
    // TODO: Replace timeout-based wait with deterministic signal/event. Bekannte technische Schuld, bereits in Arbeit.
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Gemeinsamer Sende-Kern mit sendMessage: Content aufbereiten, verschlüsseln/hochladen
    // und senden (inkl. einheitlichem MatrixError-Mapping).
    const messageId = await this.prepareAndDispatchMessage(roomId, directMessage);

    // TODO: Replace timeout-based wait with deterministic sync before querying the sent message id. Bekannte technische Schuld, bereits in Arbeit.
    await sleep(5000);

    return this.getExtendedMessage(roomId, messageId);
  }

  /**
   * Beim Senden trägt das ausgehende Event keine interne fileId (wird vor dem Senden
   * entfernt), und die Response wird ohne Media-Staging erzeugt. Damit der Aufrufer das
   * Attachment weiter über die ihm bereits bekannte fileId (aus postMediaFile) ansprechen
   * kann, referenziert die Response dieselbe fileId wie der Request. Ohne Attachment ein No-op.
   */
  private echoRequestFileId(message: ApiMessage, request: MessageContent): ApiMessage {
    if (request.fileId) {
      message.fileId = request.fileId;
    }
    return message;
  }

  /**
   * Gemeinsamer Netz-Versand-Kern: sendet den fertigen Event-Content und bildet
   * MatrixError einheitlich ab. Wird von shared-api sendMessage sowie den App-only-
   * Erweiterungen sendCustomMessage/sendReply genutzt, damit alle Sendepfade denselben
   * Versand- und Fehlerpfad durchlaufen (den auch der Testtreiber über sendMessage testet).
   */
  private async dispatchMessageEvent(roomId: string, content: RoomMessageEventContent): Promise<string> {
    try {
      const { event_id: messageId } = await this.matrix.client.sendMessage(roomId, content);
      return messageId;
    } catch (error) {
      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('SendMessageError', error);
    }
  }

  /**
   * Gemeinsamer Sende-Kern für Raum- und Direktnachrichten: bestimmt die Raum-
   * verschlüsselung, bereitet den Content auf (Attachment-Upload via prepareMessageContent)
   * und sendet über dispatchMessageEvent. Gibt die Event-ID zurück. Genutzt von sendMessage
   * und sendDirectMessageInternal, damit beide Wege identischen Aufbereitungs-, Versand- und
   * Fehlerpfad teilen.
   */
  private async prepareAndDispatchMessage(roomId: string, messageContent: MessageContent): Promise<string> {
    const isRoomEncrypted = !!this.matrix.client.getCrypto()?.isEncryptionEnabledInRoom(roomId ?? '');
    const content = await this.prepareMessageContent(roomId, messageContent, isRoomEncrypted);
    return this.dispatchMessageEvent(roomId, content);
  }

  private addMentions(content: RoomMessageEventContent, mentionsList: string[]): void {
    const mentionsContent: IMentions = {};
    const userMentions = new Set<string>();
    mentionsList.forEach(value => {
      if (value === 'room') {
        mentionsContent.room = true;
      } else {
        userMentions.add(value);
      }
    });
    mentionsContent.user_ids = [...userMentions];
    content['m.mentions'] = mentionsContent;
  }

  /**
   * Get single extended message
   * @param roomId ID of the room
   * @param messageId ID of the message
   */
  private async getMessageEvent(roomId: string, messageId: string): Promise<MatrixEvent> {
    if (!roomId) {
      throw new Error('Required parameter roomId was null or undefined when calling getMessage.');
    }
    if (!messageId) {
      throw new Error('Required parameter messageId was null or undefined when calling getMessage.');
    }

    const room = this.matrix.client.getRoom(roomId);
    if (!room) {
      throw new Error('No room was found for id ${roomId} when calling getMessages.');
    }
    const event = room.findEventById(messageId);
    if (!event) {
      throw new Error('No message with id ${messageId} was found in room ${roomId} when calling getMessage.');
    }
    return event;
  }

  private async createDirectRoomAndInvite(mxid: string): Promise<string> {
    const username = mxid.split(':')[0];
    const roomOptions: ICreateRoomOpts = {
      creation_content: { type: 'de.gematik.tim.roomtype.default.v1' },
      initial_state: [
        {
          content: {},
          state_key: '',
          type: 'de.gematik.tim.room.default.v1'
        },
        {
          content: { guest_access: 'can_join' },
          state_key: '',
          type: 'm.room.guest_access'
        },
        {
          content: { algorithm: 'm.megolm.v1.aes-sha2' },
          type: 'm.room.encryption',
          state_key: ''
        },
        {
          content: { name: `Chat with ${username}` },
          type: 'de.gematik.tim.room.name',
          state_key: ''
        },
        {
          content: { name: `Chat with ${username}` },
          type: 'm.room.name',
          state_key: ''
        },
        {
          content: { topic: 'Direct message' },
          type: 'de.gematik.tim.room.topic',
          state_key: ''
        },
        {
          content: { topic: 'Direct message' },
          type: 'm.room.topic',
          state_key: ''
        },
        {
          content: { history_visibility: HistoryVisibility.Invited },
          type: 'm.room.history_visibility',
          state_key: ''
        }
      ],
      visibility: Visibility.Private,
      preset: Preset.TrustedPrivateChat,
      is_direct: true,
      invite: [mxid]
    };

    try {
      const { room_id } = await this.matrix.client.createRoom(roomOptions);

      return room_id;
    } catch (error) {
      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('CreateRoomError', error);
    }
  }

  // use for editMessage
  /**
   * Extrahiert alle Links aus einem HTML-String.
   *
   * @param roomId die ID des Rooms
   * @param textBody der Nachrichteninhalt
   * @param htmlBody Der HTML-Inhalt als String.
   * @param originalMessageId
   * @returns Eine Liste der gefundenen Links.
   */
  private async sendEditEvent(
    roomId: string | undefined,
    textBody: string | undefined,
    originalMessageId: string,
    htmlBody: string
  ): Promise<MatrixEvent> {
    if (!textBody || !roomId) {
      throw new Error('Required parameters are null or undefined.');
    }

    const content = this.createReplacementContent(roomId, textBody, originalMessageId, htmlBody);

    try {
      const { event_id: messageId } = await this.matrix.client.sendEvent(roomId, EventType.RoomMessage, content);

      return this.getMessageEvent(roomId, messageId);
    } catch (error) {
      if (!(error instanceof MatrixError)) {
        throw error;
      }
      throw matrixErrorAsJson('SendEventError', error);
    }
  }

  private createReplacementContent(
    roomId: string,
    textBody: string,
    originalMessageId: string,
    htmlBody: string
  ): RoomMessageEventContent {
    const clientSync = this.matrix.getClientSync();
    if (!clientSync) {
      throw new Error('NoClient');
    }
    const room = clientSync.getRoom(roomId);
    if (!room) {
      throw new Error('NoRoom');
    }
    const messageEvent = room.findEventById(originalMessageId);
    if (!messageEvent) {
      throw new Error('NoMessage');
    }
    const originalMessageContent = messageEvent.getContent();
    const mentionsList = originalMessageContent?.['m.mentions']?.user_ids ?? [];
    const content: RoomMessageEventContent = {
      msgtype: MsgType.Text,
      body: `* ${textBody}`,
      formatted_body: `* ${htmlBody}`,
      'm.new_content': {
        msgtype: MsgType.Text,
        body: textBody,
        formatted_body: htmlBody || undefined
      },
      'm.relates_to': {
        rel_type: RelationType.Replace,
        event_id: originalMessageId ?? ''
      }
    };
    this.addMentions(content['m.new_content'], mentionsList || []);
    return content;
  }

  /**
   * Erstellt den Nachrichteninhalt im benötigten Format.
   *
   * @param textBody Der Textinhalt der Nachricht.
   * @param htmlBody Der formatierte HTML-Inhalt der Nachricht.
   * @param mentionsList die Liste der Erwähnungen
   * @returns Der Nachrichteninhalt als RoomMessageEventContent.
   */
  private createMessageContent(textBody: string, htmlBody?: string, mentionsList?: string[]): MessageContent {
    let content: RoomMessageEventContent = {
      msgtype: MsgType.Text,
      body: textBody
    };
    if (htmlBody) {
      content = {
        msgtype: MsgType.Text,
        body: textBody,
        formatted_body: htmlBody,
        format: 'org.matrix.custom.html'
      };
    }
    if (mentionsList) {
      this.addMentions(content, mentionsList);
    }

    return content;
  }

  /**
   * Baut den Media-Event-Content einer Datei zusammen und lädt sie hoch. Wird von beiden
   * Sende-Pfaden genutzt: der App-UI ({@link sendMessageWithAttachments}) und dem
   * Testtreiber ({@link sendMessage} mit fileId über {@link prepareMessageContent}).
   * Damit durchlaufen App und Testtreiber denselben Assembly- und Upload-Code (A_25363).
   */
  private async buildAndUploadMediaContent(
    roomId: string,
    file: File,
    isRoomEncrypted: boolean,
    preset?: Partial<ExtendedMediaEventContent>
  ): Promise<RoomMessageEventContent> {
    const content = await this.fileService.mergeMediaEventContent(file, preset);

    return this.fileService.prepareMediaEventContent(roomId, file, content, isRoomEncrypted);
  }

  /**
   * takes into account a file attachment, encrypts it - if required by the room - and uploads it
   * @param messageContent
   * @param isRoomEncrypted
   */
  private async prepareMessageContent(
    roomId: string,
    messageContent: MessageContent,
    isRoomEncrypted = true
  ): Promise<RoomMessageEventContent> {
    // there is an attachment
    if (messageContent.fileId) {
      const blob = this.fileService.mediaStorage.get(messageContent.fileId);
      if (!blob) {
        throw new Error(`Kein Blob im mediaStorage für fileId ${messageContent.fileId}`);
      }

      // fileId ist eine testtreiber-interne Kennung (Schlüssel im mediaStorage) und
      // gehört nicht in den ausgehenden Matrix-Event-Content. Der Empfänger leitet
      // seine eigene fileId beim Empfang aus file.url ab (siehe eventToApiMessage).
      const preset = { ...messageContent };
      delete preset.fileId;

      // Datei aus dem gespeicherten Roh-Blob rekonstruieren, damit der Testtreiber-Pfad
      // dieselbe Assembly (mergeMediaEventContent) durchläuft wie die App-UI. Der body
      // trägt den ursprünglichen Dateinamen; Fallback analog zu MediaService.postMediaFile.
      // postMediaFile hat das Blob nur lokal in mediaStorage abgelegt (kein Server-Upload);
      // der eigentliche - in verschlüsselten Räumen verschlüsselte - Upload passiert erst
      // hier beim Senden.
      const file = new File([blob], messageContent.body || 'file123', { type: blob.type });

      return this.buildAndUploadMediaContent(
        roomId,
        file,
        isRoomEncrypted,
        preset as Partial<ExtendedMediaEventContent>
      );
    } else return messageContent as RoomMessageEventContent;
  }
}

function eventToApiMessage(event: MatrixEvent): ApiMessage {
  const content = event.getContent();
  const msgtype = content.msgtype as MsgType;
  const hasFile = ([MsgType.File, MsgType.Image, MsgType.Audio, MsgType.Video] as MsgType[]).includes(msgtype);
  // Eine editierte Nachricht wird durch ein m.replace-Event repräsentiert, das eine eigene
  // Event-ID trägt. Die stabile Identität der Nachricht ist aber das Original-Event. Wir geben
  // daher als messageId die Original-Event-ID zurück (aus m.relates_to), nicht die des Edits.
  // Andernfalls würde z.B. deleteMessage nur das Edit-Event redacten und die Originalnachricht
  // (mit dem with_rel_types-Cascade) bliebe bestehen – sichtbar als Rückfall auf den Originaltext
  // bei spec-konformen Gegenstellen (anderer Homeserver).
  const relatesTo = content['m.relates_to'];
  const messageId =
    relatesTo?.rel_type === RelationType.Replace && relatesTo.event_id ? relatesTo.event_id : event.getId();
  return {
    event_id: event.getId() ?? '',
    room_id: event.getRoomId(),
    type: event.getType(),
    messageId,
    timestamp: event.getDate()!.toISOString(),
    origin_server_ts: event.getTs(),
    lastUpdated: event.getDate()!.toISOString(),
    author: event.getSender() ?? '',
    body: content['body'],
    geo_uri: content['geo_uri'],
    msgtype,
    fileId: hasFile ? content['fileId'] : undefined,
    info: {
      mimetype: content['info']?.mimetype,
      size: content['info']?.size
    } as MessageContentInfo,
    filename: hasFile ? content['filename'] : undefined,
    file: content['file']
      ? {
          url: content['file'].url, // mxc://... url to the file
          v: content['file'].v, // usually "v2"
          key: content['file'].key, // encryption key object
          iv: content['file'].iv, // initialization vector
          hashes: content['file'].hashes // hash map
        }
      : undefined
  };
}

function eventToMessage(roomId: string, event: MatrixEvent): Message {
  return {
    ...eventToApiMessage(event),
    roomId,
    formattedBody: event.getContent()?.['formatted_body'],
    editedMessage: event.getContent()?.['m.new_content']
  };
}
